import { motion } from "framer-motion";
import { MessageCircle, Phone, Mail, MapPin } from "lucide-react";
import { COMPANY } from "@/data/products";
import { waGeneral } from "@/lib/whatsapp";

export default function ContactPage() {
    return (
        <main className="bg-bone text-ink" data-testid="page-contact">
            <section className="pt-32 md:pt-40 pb-20 md:pb-28">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">
                        Home · 05 / Contact
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="mt-4 font-display text-6xl md:text-9xl leading-[0.9] tracking-[-0.02em]"
                    >
                        Say <span className="italic text-brand-red">hello.</span>
                    </motion.h1>
                    <p className="mt-6 max-w-xl text-ink/70 text-lg">
                        Whether it's one shade for a nursery or an entire tower façade — reach out
                        and we'll match you with the right coating.
                    </p>
                </div>
            </section>

            <section className="pb-24 md:pb-32">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-12 gap-6">
                    <a
                        href={waGeneral()}
                        target="_blank"
                        rel="noreferrer"
                        className="col-span-12 md:col-span-8 group relative overflow-hidden bg-brand-red text-bone p-10 md:p-16 min-h-[320px] flex flex-col justify-between"
                        data-testid="contact-card-whatsapp"
                    >
                        <div className="flex items-center justify-between">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-bone/70">
                                Preferred · fastest
                            </div>
                            <MessageCircle />
                        </div>
                        <div>
                            <div className="font-display text-5xl md:text-7xl leading-[0.95] tracking-[-0.02em]">
                                WhatsApp us.
                            </div>
                            <div className="mt-4 text-bone/80 max-w-md">
                                Same-day replies, Sunday to Thursday. Share a photo of your wall and
                                we'll suggest three shades on the house.
                            </div>
                            <div className="mt-8 inline-flex items-center gap-2 border-b border-bone/50 pb-1 text-sm font-mono uppercase tracking-widest">
                                Open Chat →
                            </div>
                        </div>
                    </a>

                    <div className="col-span-12 md:col-span-4 grid gap-6">
                        <a
                            href={`tel:${COMPANY.phoneRaw}`}
                            className="bg-ink text-bone p-8 hover:bg-brand-orange transition-colors"
                            data-testid="contact-card-call"
                        >
                            <Phone size={20} />
                            <div className="mt-6 font-display text-3xl">Call</div>
                            <div className="mt-2 text-bone/70">{COMPANY.phone}</div>
                        </a>
                        <a
                            href={`mailto:${COMPANY.email}`}
                            className="bg-cream p-8 hover:bg-brand-yellow transition-colors border border-ink/10"
                            data-testid="contact-card-email"
                        >
                            <Mail size={20} />
                            <div className="mt-6 font-display text-3xl">Email</div>
                            <div className="mt-2 text-ink/70">{COMPANY.email}</div>
                        </a>
                    </div>

                    <div className="col-span-12 mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-ink/15 pt-10">
                        <div>
                            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink/50">
                                <MapPin size={12} /> Studio
                            </div>
                            <address className="mt-3 not-italic font-display text-2xl leading-snug space-y-1">
                                {COMPANY.addressLines.map((line) => (
                                    <div key={line}>{line}</div>
                                ))}
                            </address>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">
                                Hours
                            </div>
                            <div className="mt-3 font-display text-2xl">
                                Sun–Thu · 08:00 – 17:00
                            </div>
                            <div className="text-ink/60 text-sm mt-1">Fri–Sat · Closed</div>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">
                                Web
                            </div>
                            <div className="mt-3 font-display text-2xl">
                                {COMPANY.website}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
